<template>
  <div>
    <!-- Filter Box -->
    <div class="has-background-white has-shadow pa-md mb-lg">
      <div class="columns is-vcentered">
        <div class="column">
          <OrganizationSelect v-model="organization" />
        </div>
        <div class="column">
          <BillingPeriodSelect v-model="focusBillingPeriod" :size="null" />
        </div>
        <div class="column is-narrow">
          <b-button
            type="is-primary"
            outlined
            expanded
            :loading="loading"
            :disabled="(!organization) || loading"
            @click="loadReport"
          >Run Report</b-button>
        </div>
      </div>
    </div>
    <div v-if="title">
      <!-- Title Box -->
      <div class="has-background-white has-shadow pa-md mb-lg">
        <p v-if="title" class="has-text-weight-bold is-size-4">{{ title.name }}</p>
        <p v-else>Loading...</p>
      </div>
      <!-- Organization Summary -->
      <div class="columns">
        <!-- Plan -->
        <div class="column">
          <div class="has-background-white has-shadow pa-md mb-lg">
            <p class="mb-sm has-text-centered has-text-grey has-text-weight-bold">Plan</p>
            <div class="border">
              <table class="table is-fullwidth is-really-narrow is-size-8 is-paddingless is-striped">
                <tbody>
                  <tr>
                    <th class="has-text-grey has-text-weight-normal">Budget</th>
                    <td class="has-text-right">
                      <Currency
                        :value="organizationSummary.amount_budgeted"
                        :compare="false"
                        :mantissa="2" />
                    </td>
                  </tr>
                  <tr>
                    <th class="has-text-grey has-text-weight-normal">Funding</th>
                    <td class="has-text-right">
                      <Currency
                        :value="organizationSummary.amount_funded"
                        :compare="false"
                        :mantissa="2" />
                    </td>
                  </tr>
                  <tr>
                    <th class="has-text-grey has-text-weight-normal">Allocated</th>
                    <td class="has-text-right">
                      <Currency
                        :value="organizationSummary.amount_allocated"
                        :compare="false"
                        :mantissa="2" />
                    </td>
                  </tr>
                  <tr>
                    <th class="has-text-grey has-text-weight-normal">Unallocated</th>
                    <td class="has-text-right">
                      <Currency
                        :value="organizationSummary.amount_unallocated"
                        :compare="false"
                        :mantissa="2" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <!-- Delivery -->
        <div class="column">
          <div class="has-background-white has-shadow pa-md mb-lg">
            <p class="mb-sm has-text-centered has-text-grey has-text-weight-bold">Delivery</p>
            <div class="border">
              <table class="table is-fullwidth is-really-narrow is-size-8 is-paddingless is-striped">
                <tbody>
                  <tr>
                    <th class="has-text-grey has-text-weight-normal">Delivered</th>
                    <td class="has-text-right">
                      <Currency
                        :value="organizationSummary.amount_fulfilled"
                        :compare="false"
                        :mantissa="2" />
                    </td>
                  </tr>
                  <tr>
                    <th class="has-text-grey has-text-weight-normal">Cost</th>
                    <td class="has-text-right">
                      <Currency
                        :value="organizationSummary.amount_fulfilled_cost"
                        :compare="false"
                        :mantissa="2" />
                    </td>
                  </tr>
                  <tr>
                    <th class="has-text-grey has-text-weight-normal">Remaining</th>
                    <td class="has-text-right">
                      <Currency :value="Number(organizationSummary.amount_budgeted) - Number(organizationSummary.amount_fulfilled)" :mantissa="2" />
                    </td>
                  </tr>
                  <tr>
                    <th class="has-text-grey has-text-weight-normal">Margin</th>
                    <td class="has-text-right">
                      <Margin :revenue="organizationSummary.amount_fulfilled" :cogs="organizationSummary.amount_fulfilled_cost" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <!-- Billing -->
        <div class="column">
          <div class="has-background-white has-shadow pa-md mb-lg">
            <p class="mb-sm has-text-centered has-text-grey has-text-weight-bold">Billing</p>
            <div class="border">
              <table class="table is-fullwidth is-really-narrow is-size-8 is-paddingless is-striped">
                <tbody>
                  <tr>
                    <th class="has-text-grey has-text-weight-normal">Scheduled</th>
                    <td class="has-text-right">
                      <Currency
                        :value="organizationSummary.amount_scheduled"
                        :compare="false"
                        :mantissa="2" />
                    </td>
                  </tr>
                  <tr>
                    <th class="has-text-grey has-text-weight-normal">Billed</th>
                    <td class="has-text-right">
                      <Currency
                        :value="organizationSummary.amount_billed"
                        :compare="false"
                        :mantissa="2" />
                    </td>
                  </tr>
                  <tr>
                    <th class="has-text-grey has-text-weight-normal">Remaining</th>
                    <td class="has-text-right">
                      <Currency :value="Number(organizationSummary.amount_budgeted) - Number(organizationSummary.amount_billed)" :mantissa="2" />
                    </td>
                  </tr>
                  <tr>
                    <th class="has-text-grey has-text-weight-normal">Prebill Balance</th>
                    <td class="has-text-right">
                      <Currency
                        :class="(Number(organizationSummary.amount_budgeted - organizationSummary.amount_allocated) < 0) ? 'has-text-danger' : 'null'"
                        :value="Number(organizationSummary.amount_budgeted - organizationSummary.amount_allocated)"
                        :mantissa="2" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <!-- Expenses -->
        <div class="column">
          <div class="has-background-white has-shadow pa-md mb-lg">
            <p class="mb-sm has-text-centered has-text-grey has-text-weight-bold">Expenses</p>
            <div class="border">
              <table class="table is-fullwidth is-really-narrow is-size-8 is-paddingless is-striped">
                <tbody>
                  <tr>
                    <th class="has-text-grey has-text-weight-normal">Budget</th>
                    <td class="has-text-right">
                      XX
                    </td>
                  </tr>
                  <tr>
                    <th class="has-text-grey has-text-weight-normal">Pending</th>
                    <td class="has-text-right">
                      <Currency
                        :value="organizationSummary.amount_expenses_pending"
                        :compare="false"
                        :mantissa="2" />
                    </td>
                  </tr>
                  <tr>
                    <th class="has-text-grey has-text-weight-normal">Approved</th>
                    <td class="has-text-right">
                      <Currency
                        :value="Number(organizationSummary.amount_expenses_billable) + Number(organizationSummary.amount_expenses_nonbillable)"
                        :compare="false"
                        :mantissa="2" />
                    </td>
                  </tr>
                  <tr>
                    <th class="has-text-grey has-text-weight-normal">Remaining</th>
                    <td class="has-text-right">
                      XX
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <!-- Billing Period Summary -->
      <div class="has-background-white has-shadow pa-md mb-lg">
        <table v-if="organizationMetrics.length" class="table is-fullwidth">
          <tr>
            <th class="is-size-8">Metric</th>
            <th v-for="period in billingPeriods" :key="period.id" class="is-size-8 has-text-right">{{ period.short_label }}</th>
          </tr>
          <tr v-for="(row, index) in rows" :key="index">
            <td class="is-size-8">{{ row.label }}</td>
            <td v-for="(metric, index) in organizationMetrics" :key="index" class="is-size-8 has-text-right">
              <p v-if="metric[row.key]"><Currency :value="metric[row.key]" /></p>
              <p v-else><Currency :value="0" /></p>
            </td>
          </tr>
        </table>
      </div>
      <!-- Project Summary -->
      <div>
        <div class="has-text-grey has-text-weight-bold mb-md">ALL PROJECTS</div>
        <div v-for="project in projects" class="">
          <Project :baseProject="project" :billingPeriods="billingPeriods" :focusBillingPeriod="focusBillingPeriod" />
        </div>
      </div>
    </div>
    <div class="has-background-white has-shadow pa-lg mb-lg has-text-centered has-text-grey-light is-size-8 " v-else>
      Select an organization to run a report.
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import BillingPeriodSelect from '@/components/Models/BillingPeriod/SelectMenu'
import OrganizationSelect from '@/components/Models/Organization/SelectMenu'
import Project from '../components/ConsolidatedReport/Project'

export default {
  components: {
    BillingPeriodSelect,
    OrganizationSelect,
    Project
  },
  computed: {
    ...mapGetters(['api', 'context']),
    loading () {
      return false
    }
  },
  data () {
    return {
      billingPeriods: [],
      startBillingPeriod: null,
      endBillingPeriod: null,
      focusBillingPeriod: null,
      loadingOrganizationSummary: false,
      loadingProjectSummary: false,
      startBillingPeriodId: 1043,
      endBillingPeriodId: 1054,
      organization: null,
      period: null,
      title: null,
      organizationSummary: null,
      organizationMetrics: [],
      projects: null,
      rows: [{
        label: 'Pending',
        key: 'amount_pending'
      },{
        label: 'Delivered',
        key: 'amount_pending'
      },{
        label: 'Scheduled',
        key: 'amount_scheduled'
      }, {
        label: 'Billed',
        key: 'amount_billed'
      }]
    }
  },
  methods: {
    loadReport () {
      this.loadOrganizationSummary()
      this.loadProjectSummary()
    },
    loadOrganizationSummary () {
      this.loadingOrganizationSummary = true
      const endpoint = this.api.route + '/admin/reports/organization-period'
      const query = {
        params: {
          organization_id: this.organization.id,
          start_billing_period_id: 1043,
          end_billing_period_id: 1054
        }
      }
      this.$http.get(endpoint, query).then(response => {
        this.organizationMetrics = []
        this.organizationMetrics = response.data.periods
        this.title = response.data.organization
        this.projects = response.data.projects
        this.organizationSummary = response.data.summary
        this.loadingOrganizationSummary = false
      })
    },
    loadProjectSummary() {

    },
    loadBillingPeriods () {
      this.loadingBillingPeriods = true
      const start = this.$dates(this.year).startOf('year')
      const end = this.$dates(this.year).endOf('year')
      const endpoint = this.api.base + 'billing-periods'
      const query = {
        params: {
          start: start.format('YYYY-MM-DD'),
          end: end.format('YYYY-MM-DD'),
          order_by: 'asc'
        }
      }
      this.$http.get(endpoint, query).then(response => {
        this.billingPeriods = response.data
        this.startBillingPeriod = this.billingPeriods[0]
        this.endBillingPeriod = this.billingPeriods[this.billingPeriods.length - 1];
      })
    },
  },
  mounted () {
    this.loadBillingPeriods()
  }
}
</script>
